import { http } from '../../http';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import { __ } from '@/js/stores/expressions';

export type StreamingFieldDefinition = {
    key: string;
    label: string;
    type: 'text' | 'select' | 'password' | 'textarea';
    required: boolean;
    defaultValue?: string;
    options?: string[];
    canImportFromV1: boolean;
    templateVariables?: StreamingTemplateVariable[] | null;
    helpText?: string | null;
};

export type StreamingTemplateVariable = {
    token: string;
    description: string;
};

export type V1IntegrationEndpoint = {
    name: string;
    endpoint: string;
};

export type StreamingProviderOption = {
    key: string;
    name: string;
    endpoint: string;
    fields: StreamingFieldDefinition[];
    v1Integrations?: V1IntegrationEndpoint[];
};

export type V1StreamingConfig = {
    rules?: { skillId: string | null; queueId: string | null; channelId: string | null }[];
    fieldDescriptions?: Record<string, string>;
    [key: string]: unknown;
};

export type StreamingRule = {
    queueId: string | null;
    skillId: string | null;
    channelId: string | null;
    isEnabled: boolean;
};

export type StreamingFilterOption = {
    id: string;
    name: string;
};

export type StreamingTenantFilterOptions = {
    skills: StreamingFilterOption[];
    queues: StreamingFilterOption[];
    channels: StreamingFilterOption[];
};

export type StreamingRuleValue = {
    skillId: string | null;
    queueId: string | null;
    channelId: string | null;
    isEnabled: boolean;
    maxStreamingDays: number | null;
};

export type StreamingConfigSummary = {
    providerId: string;
    name: string;
    providerKey: string;
    endpoint: string;
    configSummary: string | null;
};

export type ExistingStreamingConfig = {
    providerId: string;
    name: string;
    providerKey: string;
    fieldValues: Record<string, string>;
    streamingRules: StreamingRuleValue[];
    storedSecrets: string[];
};

export const getStreamingTenantFilterOptions = async (
    tenantId: string
): Promise<StreamingTenantFilterOptions> => {
    return http
        .backoffice()
        .get<StreamingTenantFilterOptions>(
            `audio-streaming/tenant-filter-options/${tenantId}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-streaming-config') } })
        )
        .then((response) => response.data);
};

export const getStreamingConfigs = async (tenantId: string): Promise<StreamingConfigSummary[]> => {
    return http
        .backoffice()
        .get<StreamingConfigSummary[]>(
            `audio-streaming/${tenantId}/configs`,
            useCustomConfig({ errorMessage: { 500: __('back-office-streaming-config') } })
        )
        .then((response) => response.data);
};

export const getStreamingConfig = async (
    endpoint: string,
    tenantId: string,
    providerId: string
): Promise<ExistingStreamingConfig | null> => {
    return http
        .backoffice()
        .get<ExistingStreamingConfig>(`${endpoint}/${tenantId}/${providerId}`, { silent: true })
        .then((response) => response.data)
        .catch((error) => {
            if (error?.response?.status === 404) return null;
            throw error;
        });
};

export const deleteStreamingConfig = async (providerId: string): Promise<void> => {
    return http
        .backoffice()
        .delete(
            `audio-streaming/${providerId}`,
            useCustomConfig({ errorMessage: { 500: __('back-office-streaming-config') } })
        )
        .then(() => undefined);
};

export const updateStreamingConfig = async (
    endpoint: string,
    providerId: string,
    data: Record<string, unknown>
): Promise<void> => {
    return http
        .backoffice()
        .put(
            `${endpoint}/${providerId}`,
            data,
            useCustomConfig({ errorMessage: { 500: __('back-office-streaming-config') } })
        )
        .then(() => undefined);
};

export const getStreamingProviderOptions = async (): Promise<StreamingProviderOption[]> => {
    return http
        .backoffice()
        .get<StreamingProviderOption[]>(
            'audio-streaming/options',
            useCustomConfig({ errorMessage: { 500: __('back-office-streaming-config') } })
        )
        .then((response) => response.data);
};

export const getV1StreamingConfig = async (
    tenantId: string,
    v1Integration: string
): Promise<V1StreamingConfig | null> => {
    return http
        .backoffice()
        .get<V1StreamingConfig>(
            `${v1Integration}${v1Integration.includes('?') ? '&' : '?'}tenantId=${encodeURIComponent(tenantId)}`,
            { silent: true }
        )
        .then((response) => response.data)
        .catch((error) => {
            if (error?.response?.status === 404) return null;
            throw error;
        });
};

export const createStreamingConfig = async (
    endpoint: string,
    data: Record<string, unknown>
): Promise<void> => {
    return http
        .backoffice()
        .post(
            endpoint,
            data,
            useCustomConfig({ errorMessage: { 500: __('back-office-streaming-config') } })
        )
        .then(() => undefined);
};
